import { useQuery } from "@apollo/client";
import { Box, Carousel, Text } from "grommet";
import { Favorite } from "grommet-icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSpring } from "react-spring";
import { GET_CUM_COUNTER, GET_DENY_STATUS } from "../../queries/user_queries";
import DenyStatus from "../DenyStatus";
import { rankItemTheme } from "../rankItemTheme";
import "../style.css";
import DenyStatusProgressBars from "./DenyStatusProgressBar";

// Shared height so all three carousel slides render at the same size.
const SLIDE_HEIGHT = "144px";

const CarouselOne = () => {
  const { data: cumData, startPolling } = useQuery(GET_CUM_COUNTER);
  // broadcasterName used to be sent from here, but the resolver never read
  // it - it's decided server-side now (see resolvers.ts's `broadcaster`).
  const { data: denyData, startPolling: startDenyPolling } =
    useQuery(GET_DENY_STATUS);

  const icon = <Favorite size="xlarge" color="light-1" />;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [counter, setCounter] = useState(0);
  const [advanceTrigger, setAdvanceTrigger] = useState(0);
  const prevDenyCount = useRef<number | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const gradientTwo = useMemo(() => {
    const theme = rankItemTheme;
    const { colorOne, colorTwo, colorThree } = theme;
    return `linear-gradient(-45deg, ${colorOne}, ${colorTwo}, ${colorThree})`;
  }, []);

  useEffect(() => {
    startPolling(2000);
    startDenyPolling(1000);
  }, [startPolling, startDenyPolling]);

  useEffect(() => {
    if (cumData) {
      setCounter(cumData.getCumCounter);
    }
  }, [cumData]);

  // Monitor deny status changes
  useEffect(() => {
    if (denyData?.getDenyStatus) {
      const { numberOfDenies, numberOfUndenies } = denyData.getDenyStatus;
      const currentTotal = numberOfDenies + numberOfUndenies;

      if (
        prevDenyCount.current !== null &&
        prevDenyCount.current !== currentTotal
      ) {
        // Deny status changed, jump to deny status view (index 2).
        // Also bump advanceTrigger so the auto-advance effect restarts its
        // timer even if currentIndex was already 2 (setCurrentIndex would be
        // a no-op in that case since the value doesn't change).
        setCurrentIndex(2);
        setAdvanceTrigger((prev) => prev + 1);
      }

      prevDenyCount.current = currentTotal;
    }
  }, [denyData]);

  const { gradientVal } = useSpring({
    gradientVal: 100,
    from: { gradientVal: 0 },
    config: {
      mass: 20,
      friction: 0,
      tension: 20,
    },
  });

  // Cum Counter Slide
  const cumCounterSlide = (
    <Box fill key="counter" justify="center">
      <Box
        round="large"
        style={{
          background: gradientTwo,
          backgroundSize: "600% 600%",
          WebkitAnimation: "Gradient 10s ease infinite",
          MozAnimation: "Gradient 10s ease infinite",
          animation: "Gradient 10s ease infinite",
        }}
        border={{ size: "medium", color: "accent-2" }}
        width="full"
        height={SLIDE_HEIGHT}
        justify="center"
      >
        <Box
          animation={["fadeIn"]}
          margin="small"
          direction="row"
          align="center"
          justify="center"
          gap="xlarge"
        >
          <Box
            align="center"
            justify="center"
            style={{ backgroundColor: "rgba(193, 76, 193, .2)" }}
            pad="small"
            round="large"
            direction="row"
            gap="small"
          >
            <Box pad="small" animation={{ duration: 1000, type: "pulse" }}>
              {icon}
            </Box>
            <Text
              size="xxlarge"
              weight={"bold"}
              color={"light-1"}
              style={{
                WebkitTextStroke: "2px purple",
                paintOrder: "stroke fill",
              }}
            >
              Cum Counter: {counter}
            </Text>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  // Deny Progress Bars Slide
  const denyProgressSlide = (
    <Box fill key="progress" justify="center">
      <DenyStatusProgressBars isWidget={true} />
    </Box>
  );

  // Deny Status Slide
  const denyStatusSlide = (
    <Box fill key="status" justify="center">
      <DenyStatus isWidget={true} />
    </Box>
  );

  const slides = [cumCounterSlide, denyProgressSlide, denyStatusSlide];
  const duration = 7000;

  // Auto-advance carousel
  useEffect(() => {
    timerRef.current = setTimeout(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % 3);
    }, duration);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [currentIndex, advanceTrigger]);

  return (
    <Carousel
      fill
      activeChild={currentIndex}
      controls={false}
      children={slides}
    />
  );
};

export default CarouselOne;
